import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { DataService } from '../../services/data.service';
import { LocalizationService } from '../../services/localization.service';
import { Websocket2Registration, Websocket2Service } from '../../services/websocket2.service';
import { PgUserStatusService } from '../../pg-ui-molecules/pg-user-status.service';
import { WSServiceChatChannel } from '../pg-chat.model';
import { PgChatChannelComponent } from '../pg-chat-channel/pg-chat-channel.component';

@Component({
    selector: 'app-pg-notes',
    templateUrl: './pg-notes.component.html',
    styleUrls: ['./pg-notes.component.scss'],
    standalone: false
})
export class PgNotesComponent implements OnInit, OnDestroy {

    constructor(
        private websocketService:Websocket2Service,
        private authService:AuthService,
        private localizationService:LocalizationService,
        private dataService:DataService,
        private pgUserStatusService:PgUserStatusService
    ) {}

    /** Opaque channel id, e.g. `#note-experience-123`. Normalized with leading `#`. */
    @Input() channelId:string;

    @Input() title:string = null;

    isOpen = false;
    isLoading = false;
    isSaving = false;
    hasError = false;

    channel:{
        status: WSServiceChatChannel,
        typing: { [user:string]: number }
    } = {
        status: null,
        typing: {}
    };

    private websocketRegistration:Websocket2Registration = null;
    private _subscriptions:Array<Subscription> = [];
    private _messageAuthorWatches:Array<string> = [];
    private _resetTypingInterval = null;

    @ViewChild(PgChatChannelComponent) chatChannel:PgChatChannelComponent;

    resolveUserName = (id:string) => this.getUserName(id);

    ngOnInit(): void {
        this._resetTypingInterval = setInterval(() => {
            let _now = Date.now();
            for(let i in this.channel.typing) {
                if(this.channel.typing[i] + 3000 < _now) {
                    delete this.channel.typing[i];
                }
            }
        }, 1000);

        this.register();
    }

    ngOnDestroy(): void {
        if(this._resetTypingInterval != null) {
            clearInterval(this._resetTypingInterval);
            this._resetTypingInterval = null;
        }
        for(let _id of this._messageAuthorWatches) {
            this.pgUserStatusService.unwatch(_id);
        }
        this._messageAuthorWatches = [];
        this.unregister();
    }

    get normalizedChannelId() {
        if(this.channelId == null || this.channelId == '') return null;
        let _c = this.channelId.trim();
        if(_c == '') return null;
        if(!/^#/.test(_c)) _c = '#' + _c;
        return _c;
    }

    toggleOpen() {
        this.isOpen = !this.isOpen;
        if(this.isOpen && this.chatChannel != null) {
            setTimeout(() => {
                if(this.chatChannel != null) {
                    this.chatChannel.requestScrollToBottom(true);
                }
            });
        }
    }

    /** Badge label from WS channel history (capped display at 10+). */
    get notesCountLabel():string {
        let _count = this.channel.status?.messages?.length || 0;
        if(_count <= 0) return null;
        if(_count > 10) return '10+';
        return String(_count);
    }

    private register() {
        let _channel = this.normalizedChannelId;
        if(_channel == null || !this.authService.isLoggedIn()) {
            this.hasError = true;
            return;
        }

        this.isLoading = true;
        this.hasError = false;

        this.websocketRegistration = this.websocketService.register('notes', async () => {
            return {
                token: await this.authService.getAuthToken(),
                channel: _channel
            };
        });

        this._subscriptions.push(this.websocketRegistration.statusChange.subscribe((status) => {
            if(status == 'registered') {
                this.isLoading = false;
                this.hasError = false;
            }
            else if(status == 'unauthorized') {
                this.isLoading = false;
                this.hasError = true;
            }
        }));

        this._subscriptions.push(this.websocketRegistration.onMessage.subscribe((data) => {
            this._handleMessage(data);
        }));
    }

    private unregister() {
        for(let _sub of this._subscriptions) _sub.unsubscribe();
        this._subscriptions = [];

        if(this.websocketRegistration != null) {
            this.websocketRegistration.unregister();
            this.websocketRegistration = null;
        }
    }

    private sendNotes(data:any) {
        if(this.websocketRegistration != null) {
            this.websocketRegistration.send(data);
        }
    }

    private _handleMessage(data:any) {
        if(data == null) return;

        let _channel = this.normalizedChannelId;
        if(data.target != null && data.target != _channel) {
            return;
        }

        if(data.action == 'typing') {
            if(data.content) this.channel.typing[data.from] = Date.now();
            else if(this.channel.typing[data.from] != null) {
                delete this.channel.typing[data.from];
            }
        }
        else if(data.action == 'message') {
            if(this.channel.status != null) {
                this.channel.status.messages.push(data);
                this._ensureAuthorWatch(data.from);
                if(this.chatChannel != null) {
                    this.chatChannel.requestScrollToBottom();
                }
            }
        }
        else if(data.action == 'channel_status') {
            this.channel.status = data.content;
            if(this.channel.status != null && this.channel.status.messages != null) {
                for(let _msg of this.channel.status.messages) {
                    this._ensureAuthorWatch(_msg.from);
                }
            }
            if(this.chatChannel != null) {
                this.chatChannel.requestScrollToBottom(true);
            }
        }
        else if(data.action == 'channel_status.users') {
            if(this.channel.status != null) {
                this.channel.status.users = data.content;
            }
        }
    }

    private _ensureAuthorWatch(userId:string) {
        if(userId == null || /^guest_/.test(String(userId))) return;
        let _id = String(userId);
        if(this._messageAuthorWatches.indexOf(_id) == -1) {
            this._messageAuthorWatches.push(_id);
            this.pgUserStatusService.watch(_id);
        }
    }

    getTyping() {
        if(Object.keys(this.channel.typing).length > 0) {
            return this.channel.typing;
        }
        return null;
    }

    getUserName(id:string) {
        if(this.authService.isLoggedIn() && String(this.authService.user.id) == String(id)) {
            return this.localizationService.translate('pg-customer-care.you');
        }

        this._ensureAuthorWatch(id);
        let _name = this.pgUserStatusService.getDisplayName(id);
        return _name != null ? _name : '...';
    }

    onSendMessage(text:string) {
        let _channel = this.normalizedChannelId;
        if(_channel == null || text == null || text == '') return;

        this.sendNotes({
            action: 'message',
            target: _channel,
            content: text
        });

        this.sendNotes({
            action: 'typing',
            target: _channel,
            content: false
        });
    }

    onTypingChange(active:boolean) {
        let _channel = this.normalizedChannelId;
        if(_channel == null) return;

        this.sendNotes({
            action: 'typing',
            target: _channel,
            content: active
        });
    }

    onFilesSelected(payload:{ files:Array<File>, text:string }) {
        if(this.isSaving || payload.files == null || payload.files.length == 0) return;

        let _channel = this.normalizedChannelId;
        if(_channel == null) return;

        this.isSaving = true;
        let _message = payload.text || '';

        this.dataService.postFile('File', payload.files[0]).subscribe((data) => {
            if(data.complete) {
                this.isSaving = false;
                if(data.data != null) {
                    this.sendNotes({
                        action: 'message',
                        target: _channel,
                        content: _message,
                        data: {
                            type: 'attachment',
                            name: data.data.nome_file,
                            url: data.data.url
                        }
                    });
                }
            }
        }, () => {
            this.isSaving = false;
        });
    }

    get displayTitle() {
        if(this.title != null && this.title != '') return this.title;
        return this.normalizedChannelId || '';
    }
}
